/**
 * [ui1006, paczka KIT] REJESTRY OBSZARÓW CZTERECH STRON — jedno wejście.
 *
 * Polecenie właściciela z 5.10.2026 („cztery strony zrób od podstaw: za dużo, niepodzielone, nie do ogarnięcia”) dało każdej
 * stronie tę samą ramę: grupy w jedynym pasku i miejsca pod nim, każde pod własnym adresem. Rejestr strony jest JEDYNYM
 * źródłem tej nawigacji; ten plik zbiera cztery rejestry i daje czyste funkcje odczytu (bez Reacta, bez tras).
 *
 * Kto czyta rejestry: powłoka strony i start (paczki stron), `components/common/Obszary/adresyObszarow.js` (adresy,
 * przekierowania starych adresów), router panelu (`pages/admin/AdminModuleRouter.js` — stare slugi, przekierowanie
 * `/admin/wykaz-ksc/<x>`), „Powrót” w nagłówku aplikacji (`utils/celModuluObslugi.js`), ekran linku na telefonie
 * (`components/ProtectedRoute.js`) oraz zapadki: lustro tras (`constants/__tests__/routes.test.js`) i zapadki rejestrowe
 * N-2, N-7, N-17, N-18 (`components/common/Obszary/__tests__/zapadki/*.rejestr.test.js`).
 *
 * `aktywna: false` w rejestrze = strona żyje jeszcze po staremu; `adresObszaru` zwraca wtedy `null` i nikt nie rysuje
 * odnośnika w miejsce, którego nie ma. Stronę przełącza jej paczka (KSC, PROC, INT, O365).
 */
import wykazKsc from "./wykazKsc";
import procedury from "./procedury";
import integracje from "./integracje";
import office365 from "./office365";
import { GRUPA_STARTU, GRUPA_USTAWIEN, OBSZAR_STARTU } from "./ksztalt";

export * from "./ksztalt";

/** Cztery rejestry w kolejności stron z polecenia właściciela. */
export const REJESTRY = Object.freeze([wykazKsc, procedury, integracje, office365]);

/** Slugi czterech stron (klucze `strona` rejestrów = slugi kafelków panelu). */
export const STRONY = Object.freeze(REJESTRY.map((r) => r.strona));

const PO_STRONIE = new Map(REJESTRY.map((r) => [r.strona, r]));

/** Rejestr strony po slugu kafelka („wykaz-ksc”, „procedury-bezpieczenstwo”, „integrations”, „office365”); nieznany — null. */
export const rejestrStrony = (strona) => PO_STRONIE.get(String(strona ?? "")) || null;

/** Obszar po identyfikatorze; nieznany — null. */
export const obszarRejestru = (rejestr, id) => (rejestr ? rejestr.obszary.find((o) => o.id === id) || null : null);

/** Grupa po identyfikatorze; nieznana — null. */
export const grupaRejestru = (rejestr, id) => (rejestr ? rejestr.grupy.find((g) => g.id === id) || null : null);

/** Obszary jednej grupy w kolejności rejestru. */
export const obszaryGrupy = (rejestr, grupaId) => (rejestr ? rejestr.obszary.filter((o) => o.grupa === grupaId) : []);

/** Obszar startowy strony („Do załatwienia”). */
export const obszarStartowy = (rejestr) => obszarRejestru(rejestr, OBSZAR_STARTU);

/**
 * Pierwszy obszar grupy, do którego prowadzi adres samej grupy (np. `…/ustawienia` → „Uruchomienie”).
 * Dla grupy startu jest to start; pozycja „wkrótce” nie jest celem.
 */
export const pierwszyObszarGrupy = (rejestr, grupaId) => {
    if (grupaId === GRUPA_STARTU) return obszarStartowy(rejestr);
    return obszaryGrupy(rejestr, grupaId).find((o) => !o.wkrotce) || null;
};

/** Pierwszy obszar grupy „Ustawienia” — na każdej z czterech stron „Uruchomienie” (zapadka N-17). */
export const obszarUruchomienia = (rejestr) => pierwszyObszarGrupy(rejestr, GRUPA_USTAWIEN);

/**
 * Tytuł, pod którym człowiek TERAZ widzi stronę: po przełączeniu — tytuł z rejestru; przed przełączeniem — dotychczasowy
 * (różni się tylko w Integracjach, które zmieniają tytuł razem z układem). Lustro z tytułem kafelka pilnuje zapadka N-17.
 */
export const tytulStrony = (rejestr) => {
    if (!rejestr) return "";
    return rejestr.aktywna ? rejestr.tytul : (rejestr.tytulDotychczasowy || rejestr.tytul);
};

/**
 * KLUCZ WEJŚCIA DO MIEJSCA: własny klucz miejsca (`klucz` w rejestrze), a gdy go nie ma — klucz strony.
 * Napis albo tablica „którykolwiek” — w tej postaci przyjmuje go `hasPermission` z `useAuth`.
 *
 * [poprawka po przeglądzie 2026-10-06] Po przełączeniu strony trasa huba pyta tylko o klucz STRONY (np. Wykaz KSC:
 * `WykazKscPodglad`), więc klucz miejsca z rejestru staje się JEDYNĄ bramką frontu dla miejsc o węższym dostępie
 * („Źródła danych” — `WykazKscRedakcja`, „Dziennik modułu” — `Office365Manage`). Powłoka czyta go tą funkcją i przy
 * braku klucza rysuje odmowę z nazwą klucza (rama zostaje, pozycja nawigacji ma kłódkę) — zasada Z-09.
 * Tabelę „strona → miejsce → klucz” zamraża `config/obszary/__tests__/rejestry.test.js`.
 */
export const kluczObszaru = (rejestr, obszar) => {
    if (!rejestr) return null;
    return (obszar && obszar.klucz) || rejestr.kluczStrony || null;
};

/** Czy strona ma adresy w panelu (`/admin/<slug>/…`); Wykaz KSC ma własne drzewo `/wykaz-ksc/…`. */
export const stronaWPanelu = (rejestr) => !!rejestr && rejestr.baza.pl === `/admin/${rejestr.strona}`;

/**
 * Strona, do której należy dawny osobny slug panelu (`/admin/domains` → Integracje › Poczta).
 * @returns {{ strona: string, obszar: string } | null}
 */
export const stronaStaregoSluga = (slug) => {
    const s = String(slug ?? "");
    if (!s) return null;
    for (const rejestr of REJESTRY) {
        const obszarId = rejestr.stareSlugi && Object.prototype.hasOwnProperty.call(rejestr.stareSlugi, s) ? rejestr.stareSlugi[s] : null;
        if (obszarId) return { strona: rejestr.strona, obszar: obszarId };
    }
    return null;
};

/** Wszystkie obiekty obszaru: z zapytania (`obiekty`) i ze ścieżki (`trasyObiektow` wskazujące ten obszar). */
export const obiektyObszaru = (rejestr, obszar) => {
    if (!rejestr || !obszar) return [];
    const zeSciezki = (rejestr.trasyObiektow || []).filter((t) => t.obszar === obszar.id);
    return [...(obszar.obiekty || []), ...zeSciezki];
};

export { wykazKsc, procedury, integracje, office365 };
